import { createAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../index';

interface LongPullUIState {
  [key: string]: ILongPullUIItem[];
}

export const LONG_PULL_UI_FEATURE_KEY = 'longPullUI';

//region ACTIONS
export const addLongPullUIItem = createAction<{ key?: string; item: ILongPullUIItem }>('ADD_ITEM_TO_QUEUE');

export const updateLongPullUIItem = createAction<{
  key?: string;
  itemId: number | string;
  updates: Partial<ILongPullUIItem>;
}>('UPDATE_ITEM_IN_QUEUE');

export const removeLongPullUIItem = createAction<{ key: string; itemId: string | number }>('REMOVE_ITEM_FROM_QUEUE');

export const clearLongPullUIQueue = createAction<{ key?: string }>('CLEAR_LONG_PULL_UI_QUEUE');
//endregion

const initialState: LongPullUIState = {};

const longPullUISlice = createSlice({
  name: LONG_PULL_UI_FEATURE_KEY,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(addLongPullUIItem, (state, { payload }) => {
      const { key = 'global', item } = payload;

      // @todo: Validate payload
      if (!item) {
        console.error("Can't add to the queue: no item provided");
        return;
      }

      if (!state[key]) {
        state[key] = [];
      }

      const itemExists = state[key].some((existingItem) => existingItem.id === item.id);

      if (itemExists) {
        return;
      }

      state[key].push({ ...item, isLongpull: true });
    });

    builder.addCase(updateLongPullUIItem, (state, { payload }) => {
      const { key = 'global', itemId, updates } = payload || {};

      if (!updates) {
        console.error("Can't update the queue: no updates provided");
        return;
      }

      if (!state[key]) {
        console.error(`Queue for key: ${key} does not exist`);
        return;
      }

      const index = state[key].findIndex((queueItem) => queueItem.id === itemId);
      if (index === -1) {
        console.error(`Item with id ${itemId} not found in the queue for key: ${key}`);
        return;
      }

      const existingItem: ILongPullUIItem = state[key][index];

      for (const prop in updates) {
        if (prop !== 'id' && prop in existingItem) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          existingItem[prop] = updates[prop]!;
        }
      }
    });

    builder
      .addCase(removeLongPullUIItem, (state, { payload }) => {
        const { key, itemId } = payload || {};

        const trimmedKey = key?.trim();

        if (!itemId) {
          console.error("Can't remove from the queue: no itemId provided");
          return;
        }

        state[trimmedKey] = state[trimmedKey].filter((queueItem) => queueItem.id !== itemId);

        if (state[trimmedKey].length === 0) {
          delete state[trimmedKey];
        }
      })

      .addCase(clearLongPullUIQueue, (state, { payload }) => {
        const { key } = payload || {};

        if (typeof key === 'string') {
          if (!(key in state)) {
            console.error("Can't clear queue: invalid key provided");
            return;
          }

          delete state[key];
        } else {
          // Clear all queues
          for (const key in state) {
            delete state[key];
          }
        }
      });
  },
});

export const longPullUIReducer = longPullUISlice.reducer;

export const getLongPullUIState = (rootState: RootState) => rootState[LONG_PULL_UI_FEATURE_KEY];

const EMPTY_QUEUE: readonly any[] = [];
export const longPullUISelectors = {
  getQueueByKey: (state: RootState, key: string) => {
    const raw = getLongPullUIState(state)?.[key];
    if (!Array.isArray(raw)) return EMPTY_QUEUE;

    // only filter if there are uploads
    const hasFileUploads = raw.some((item) => item?.isFileUpload);
    if (!hasFileUploads) return raw;

    return raw.filter((item) => item && !item.isFileUpload);
  },
};
